import { memo, useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { GestureHandling } from "leaflet-gesture-handling";
import MarkerClusterGroup from "react-leaflet-cluster";
import { useIntl } from "react-intl";

import Button from "components/v2/Button/Button";
import { useGetAllShipments } from "api/queries/shipments/useGetAllShipments";
import Markers from "./Markers";
import MapLoadingSkeleton from "./MapLoadingSkeleton";

import "./MapWidget.scss";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import "leaflet/dist/leaflet.css";

L.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);

const CARTO_BASEMAP_KEY = import.meta.env.REACT_APP_CARTO_BASEMAP_KEY;

const MAP_CENTER = [35.377956, 4.89707];
const MAP_BOUNDS = [
  [-90, -180],
  [90, 180],
];

const STATUS_TO_ICON_COLOR_KEY = {
  InTransit: "Blue",
  Delayed: "Orange",
  Incomplete: "Yellow",
  Booked: "Black",
};

const getStatusCount = (markers, status) => {
  const fileNameSubstring = STATUS_TO_ICON_COLOR_KEY[status] || "Black";
  let count = 0;
  for (let i = 0; i < markers.length; i += 1) {
    if (markers[i].options.icon.options.iconUrl.includes(fileNameSubstring)) count += 1;
  }
  return count;
};

const generateClusterIcon = (cluster) => {
  const markers = cluster.getAllChildMarkers();
  const markerCount = cluster.getChildCount();

  const inTransitCount = getStatusCount(markers, "InTransit");
  const delayedCount = getStatusCount(markers, "Delayed");
  const incompleteCount = getStatusCount(markers, "Incomplete");
  const bookedCount = getStatusCount(markers, "Booked");

  // calculate the percentage of each status for the conic-gradient
  const inTransitDegreesEnd = (inTransitCount / markerCount) * 100;
  const delayedDegreesEnd = inTransitDegreesEnd + (delayedCount / markerCount) * 100;
  const incompleteDegreesEnd = delayedDegreesEnd + (incompleteCount / markerCount) * 100;
  const bookedDegreesEnd = incompleteDegreesEnd + (bookedCount / markerCount) * 100;

  return L.divIcon({
    html: `<div class="donut" style="background: conic-gradient(
    #005eac  0% ${inTransitDegreesEnd}%,
    #F1B92A ${inTransitDegreesEnd}% ${delayedDegreesEnd}%,
    #ea7939  ${delayedDegreesEnd}% ${incompleteDegreesEnd}%,
    #252525 ${incompleteDegreesEnd}% ${bookedDegreesEnd}%
    );"><div class="hole">${markerCount}</div></div>`,
    className: "marker-cluster-custom",
    iconSize: L.point(40, 40, true),
  });
};

function RefreshMapSize() {
  const map = useMap();

  useEffect(() => {
    const id = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    return () => clearTimeout(id);
  }, [map]);

  return null;
}

const MapWidget = function () {
  const intl = useIntl();
  const [markerPosition, setMarkerPosition] = useState("origin");

  const { data, isLoading } = useGetAllShipments();

  // Gets rid of leaflet tile pane in the right lower corner
  useEffect(() => {
    const controlAttribute = document.getElementsByClassName("leaflet-control-attribution");
    if (controlAttribute.length > 0) controlAttribute[0].style.display = "none";
  }, []);

  const handleToggleMarkerPosition = useCallback(() => {
    setMarkerPosition((prev) => (prev === "origin" ? "destination" : "origin"));
  }, []);

  return (
    <MapContainer
      className="map-widget"
      center={MAP_CENTER}
      zoom={2}
      minZoom={2}
      maxZoom={20}
      scrollWheelZoom
      maxBounds={MAP_BOUNDS}
      gestureHandling
    >
      <RefreshMapSize />
      {isLoading && !data?.length > 0 && <MapLoadingSkeleton />}
      <div style={{ position: "relative", height: 324, borderRadius: "0px" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAP_KEY}`}
        />
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: "1000",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            variant="secondary-gray"
            onClick={handleToggleMarkerPosition}
            label={
              markerPosition === "origin"
                ? intl.formatMessage({ id: "general.origin", defaultMessage: "Origin" })
                : intl.formatMessage({ id: "general.destination", defaultMessage: "Destination" })
            }
          />
        </div>
        <MarkerClusterGroup
          chunkedLoading
          showCoverageOnHover={false}
          maxClusterRadius={150}
          iconCreateFunction={generateClusterIcon}
        >
          <Markers mapData={data} markerPosition={markerPosition} />
        </MarkerClusterGroup>
      </div>
    </MapContainer>
  );
};

export default memo(MapWidget);
